import {
  Button,
  Container,
  DateInput,
  Flex,
  Form,
  LoadingIcon,
  Stack,
  Text,
} from "basis";
import { postCardActivate } from "library/api";
import { PageTracking } from "library/hoc/PageTracking";
import { useMutation } from "react-query";
import { CreditCard } from "../CreditCard/CreditCard";
import { SvgInline } from "../SvgInline/SvgInline";
import { parseExpiryDateResponse, validateExpiryDate } from "./validation";

const initialValues = {
  expiryDate: {
    month: "",
    year: "",
  },
};

export const ActivateCardExpiryDateComponent = ({ state, dispatch }) => {
  const activateCardMutation = useMutation(postCardActivate, {
    onSuccess: (response) => {
      const responseData = parseExpiryDateResponse(response.data);

      if (responseData === null) {
        dispatch({
          type: "INVALID_RESPONSE",
        });
      } else {
        dispatch({
          type: "EXPIRY_DATE_VERIFIED_SUCCESSFULLY",
          ...responseData,
        });
      }
    },
    onError: (error, { setErrors }) => {
      const errorType = error.response?.data?.type ?? null;

      switch (errorType) {
        case "CARD_EXPIRY_DATE_MISMATCH": {
          setErrors({
            expiryDate: "Expiry date doesn’t match our records",
          });
          break;
        }

        case "CARD_ACTIVATION_LOCKED": {
          dispatch({
            type: "CARD_ACTIVATION_LOCKED",
          });
          break;
        }

        default: {
          dispatch({
            type: "EXPIRY_DATE_VERIFICATION_FAILED",
          });
        }
      }
    },
  });
  const { accountInfo } = state;

  if (accountInfo === null) {
    return (
      <Flex height="200" placeItems="center">
        <LoadingIcon size="medium" />
      </Flex>
    );
  }

  const onSubmit = ({ errors, values, setErrors }) => {
    if (Object.keys(errors).length === 0) {
      const { expiryDate } = values;

      activateCardMutation.mutate({
        id: accountInfo.accountId,
        data: {
          card_expiry_date: {
            month: Number(expiryDate.month),
            year: 2000 + Number(expiryDate.year),
          },
        },
        setErrors,
      });
    }
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      <Stack gap="8">
        <Container bg="grey.t05" padding="4">
          <CreditCard
            cardName={accountInfo.cardName}
            cardEndsWith={accountInfo.cardEndsWith}
          />
        </Container>
        <SvgInline name="card-expiry-date" size="92" />
        <Text
          id="expiry-date"
          as="h3"
          textStyle="heading5"
          textStyle-sm="heading4"
        >
          New card expiry date
        </Text>
        <Text>
          Please check the expiry date listed on your new card and enter the
          month and year below.
        </Text>
        <Container width="100%" width-xs="324">
          <DateInput
            name="expiryDate"
            aria-labelledby="expiry-date"
            dayMode="none"
            yearMode="2-digits"
            validate={validateExpiryDate}
          />
        </Container>
        <Button
          type="submit"
          width="calc(50% - 2px)"
          width-xs="160"
          loading={activateCardMutation.isLoading}
        >
          Next
        </Button>
      </Stack>
    </Form>
  );
};

const pageData = {
  page: {
    pageType: "input-page",
    siteSection: "accounts",
    siteSubSection: "manage",
    pageTitle: "activate-card",
  },
};

export const ActivateCardExpiryDate = ({
  Component = ActivateCardExpiryDateComponent,
  ...props
}) => {
  const { product_id: productId } = props.state?.account ?? {};

  return (
    <PageTracking pageData={{ ...pageData, productId: [productId] }}>
      <Component {...props} />
    </PageTracking>
  );
};
